import { CustomState } from "@affinity/Store/Reducers";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { History } from "@insite/mobius/utilities/HistoryContext";
import * as React from "react";

/**
 * Place Order Query Model.
 * Query String properties for Place Order pre population.
 */
export type PlaceOrderQueryModel = {
    product?: string,
    quantity?: number,
    postcode?: string,
    page: "placeorder", // Discriminated union field, add to other query models
}

/**
 * Build Place Order Query.
 * Extracts query elements to model for Place Order Query String.
 * @param locationSearch location.search
 * @returns 
 */
function BuildPlaceOrderQuery(locationSearch: string): PlaceOrderQueryModel {
    return { 
        ...parseQueryString<PlaceOrderQueryModel>(locationSearch),
        page: "placeorder"
    }
}

/**
 * Build History URL.
 * State selector for URL to update history.
 * @param pathname Current URL path without query
 * @param newQueryModel Updated query model with removed segments
 * @returns 
 */
export function BuildHistoryUrl(pathname: string, newQueryModel: PlaceOrderQueryModel) {
    // Product should always be removed first so should be ignore / not set
    const queryQuantity = newQueryModel.quantity ? `quantity=${newQueryModel.quantity}`: '';
    const queryPostCode = newQueryModel.postcode ? `postcode=${encodeURI(newQueryModel.postcode)}` : '';

    if (!queryQuantity && !queryPostCode) {
        return pathname;
    }
    
    if (!queryPostCode) {
        return `${pathname}?${queryQuantity}`;
    } else {
        return `${pathname}?${queryQuantity}&${queryPostCode}`;
    }
}

/**
 * Build Persistence URL.
 * State selector for URL to store quote.
 * @param pathname Current URL path without query
 * @param queryModel Data for query segments
 * @returns 
 */
export function BuildPersitenceUrl(pathname: string, queryModel: PlaceOrderQueryModel) {
    return `${pathname}?product=${queryModel.product}&quantity=${queryModel.quantity}&postcode=${encodeURI(queryModel.postcode ?? '')}`;
}

/**
 * Populate Delegate
 * Delegate to check query string, populate data and return updated query string for return URL.
 * Return undefined for population failure
 */
export interface PopulateDelegate {
    (data: PlaceOrderQueryModel): PlaceOrderQueryModel | undefined;
}

export function BindPrePopulate(state: ApplicationState) {
    const location = getLocation(state);
    const currentUrl = location.pathname;
    const placeOrderQuery = BuildPlaceOrderQuery(location.search);
    
    return function PrePopulate(populateDelegate: PopulateDelegate, history: History): JSX.Element | undefined {
        const updatedOrderQuery = populateDelegate(placeOrderQuery);
        const success = updatedOrderQuery !== undefined;

        if (success) {
            // Update URL if query element processed
            history.push(BuildHistoryUrl(currentUrl, updatedOrderQuery));
            return (<></>);
        } else {
            // Clear URL if population failed
            if (location.search) {
                history.push(currentUrl);
            }
        }
    }
}

export function getQueryStringFromState(state: CustomState) {
    const currentPage = getCurrentPage(state);

    switch (currentPage.type) {
        case "AffinityGetQuotePage":
        case "AffinityCreateAccountPage":
            // Query String to transfer data to the Place Order page
            const result = state.affinity_query_string.PlaceOrderQueryString;
            return (result && result.product) ? result : undefined;
        default:
            // No action
            return undefined;
    }
}

export function getQueryStringFromUrl(state: CustomState) {
    const currentPage = getCurrentPage(state);
    const location = getLocation(state);
    
    switch (currentPage.type) {
        case "AffinityGetQuotePage":
        case "AffinityCreateAccountPage":
            // Query String to transfer data to the Place Order page
            return BuildPlaceOrderQuery(location.search);
        default:
            // No action
            return undefined;
    }
}

// make above a bind and return a function with state bound. IE call in state bind and use returned function in component???
// then above build URL takes pathname NOT state as bind function...

// use closure trick for caching graph queries in JS but also push to service...